import React from "react";
import HeaderPublic from "@components/layout/HeaderPublic";
import FooterPublic from "@components/layout/FooterPublic";
import {
  ArticleLayout,
  ArticleTOC,
  CtaBlock,
  FAQList,
  RelatedServicesGrid,
  SectionHead,
  ServiceHero,
} from "@components/services";
import { HeroData } from "@components/services/types";

interface ServiceTemplatePageProps {
  hero: HeroData;
  heroAddon?: React.ReactNode;
  subnav: { id: string; label: string }[];
  sections: { id: string; content: React.ReactNode }[];
  faqTitle: string;
  faqEyebrow: string;
  faqs: { question: string; answer: string }[];
  relatedServices: { tag: string; title: string; description: string; href: string }[];
  ctaBlock: {
    title: string;
    description: string;
    primaryCta: { label: string; href: string };
    secondaryCta?: {
      label: string;
      href: string;
      target?: string;
      rel?: string;
    };
  };
  orangeEyebrows?: boolean;
  /** End-of-page hand-over into the Agentic BI Guide (`GuideReadNext`);
   *  sits between "Verwandte Services" and the closing CTA on every page. */
  guideSlot: React.ReactNode;
  /** Direct line after the FAQ (`ServiceContactCard`): photo, name, role,
   *  phone. Pages with their own expert quote pass it anyway — every service
   *  page shows a person with a number. */
  contactSlot: React.ReactNode;
}

export default function ServiceTemplatePage({
  hero,
  heroAddon,
  subnav,
  sections,
  faqTitle,
  faqEyebrow,
  faqs,
  relatedServices,
  ctaBlock,
  orangeEyebrows = false,
  guideSlot,
  contactSlot,
}: ServiceTemplatePageProps) {
  return (
    <>
      <HeaderPublic />
      <ServiceHero hero={hero} />
      {heroAddon}
      <ArticleLayout toc={<ArticleTOC items={subnav} />}>
        {sections.map((section, index) => (
          /* The first section needs no top margin — the layout's own padding
             already separates it from the hero block above. */
          <section id={section.id} key={section.id} style={{ marginTop: index === 0 ? 0 : 64 }}>
            {section.content}
          </section>
        ))}
        <section id="faq" style={{ marginTop: 64 }}>
          <SectionHead eyebrow={faqEyebrow} title={faqTitle} blue={!orangeEyebrows} />
          <FAQList items={faqs} />
        </section>
        {contactSlot}
        <section style={{ marginTop: 64 }}>
          <SectionHead eyebrow="Weiter erkunden" title="Verwandte Services" blue />
          <RelatedServicesGrid items={relatedServices} />
        </section>
        {guideSlot}
        <section style={{ marginTop: 48 }}>
          <CtaBlock
            title={ctaBlock.title}
            description={ctaBlock.description}
            primaryCta={ctaBlock.primaryCta}
            secondaryCta={ctaBlock.secondaryCta}
          />
        </section>
      </ArticleLayout>
      <FooterPublic />
    </>
  );
}
